import React, { useState, useEffect, useCallback } from 'react';
import { Users, Phone, Mail, Building2, FileText, DollarSign } from 'lucide-react';
import { ContactService } from '../services/contactService';
import LoadingSpinner from './LoadingSpinner';
import ContactDetailsModal from './ContactDetailsModal';
import { Modal, ModalBody, Badge, Button, SearchBar } from './ui';

const ClientContactsModal = ({ client, isOpen, onClose }) => {
  const [allContacts, setAllContacts] = useState([]); // All contacts for this client
  const [filteredContacts, setFilteredContacts] = useState([]); // After search filter
  const [displayedContacts, setDisplayedContacts] = useState([]); // Current page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [totalPages, setTotalPages] = useState(1);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query (300ms like ContactsTable)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allContactsData = await ContactService.getAllContacts();
      const clientContacts = allContactsData.filter(contact => contact.clientId === client.id);
      console.log('ClientContactsModal: Fetched contacts for client', client.id, ':', clientContacts.length, 'contacts');
      setAllContacts(clientContacts);
      setCurrentPage(1); // Reset to first page
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [client]);

  const filterAndPaginate = useCallback(() => {
    // Apply search filter
    let filtered = allContacts;
    if (debouncedSearchQuery) {
      const searchLower = debouncedSearchQuery.toLowerCase();
      filtered = allContacts.filter(contact =>
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phoneNumber?.toLowerCase().includes(searchLower) ||
        contact.role?.toLowerCase().includes(searchLower) ||
        contact.clientName?.toLowerCase().includes(searchLower) ||
        contact.notes?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredContacts(filtered);

    // Calculate pagination
    const total = pageSize === 999999 ? 1 : Math.ceil(filtered.length / pageSize);
    setTotalPages(total);
    console.log('ClientContactsModal: Pagination -', {
      allContacts: allContacts.length,
      filtered: filtered.length,
      pageSize,
      totalPages: total,
      currentPage
    });

    // Get current page data
    if (pageSize === 999999) {
      setDisplayedContacts(filtered);
    } else {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setDisplayedContacts(filtered.slice(startIndex, endIndex));
    }
  }, [allContacts, debouncedSearchQuery, currentPage, pageSize]);

  // Fetch contacts when modal opens
  useEffect(() => {
    if (isOpen && client?.id) {
      fetchContacts();
    }
  }, [isOpen, client, fetchContacts]);

  // Filter and paginate when search or pagination changes
  useEffect(() => {
    if (allContacts.length > 0) {
      filterAndPaginate();
    } else {
      // Reset filtered contacts when no contacts
      setFilteredContacts([]);
      setDisplayedContacts([]);
      setTotalPages(1);
    }
  }, [debouncedSearchQuery, currentPage, pageSize, allContacts, filterAndPaginate]);

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setShowContactDetailsModal(true);
  };

  const handleContactUpdated = (updatedContact) => {
    if (updatedContact === null) {
      // Contact was deleted - remove from list
      setAllContacts(prev => prev.filter(c => c.id !== selectedContact.id));
    } else {
      // Contact was updated - update in list
      setAllContacts(prev => prev.map(c =>
        c.id === updatedContact.id ? updatedContact : c
      ));
    }
    setShowContactDetailsModal(false);
    setSelectedContact(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal trackAs="ClientContactsModal" isOpen={isOpen} onClose={onClose} size="xl">
        {/* Custom Header with Icon - Matching ContactDetailsModal Pattern */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--cta-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Users size={28} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {client?.name || 'Client Contacts'}
              </h1>
              <p
                style={{
                  margin: 0,
                  marginTop: 'var(--space-1)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}
              >
                {loading ? 'Loading contacts...' : `${allContacts.length} total contact${allContacts.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
        </div>

        <ModalBody style={{ padding: 0 }}>
          {/* Search Bar Toolbar - Matching ContactsTable */}
          {!loading && !error && allContacts.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-bg)',
                borderBottom: '1px solid var(--gray-600)',
                gap: 'var(--space-4)',
              }}
            >
              <div style={{ width: '300px', flexShrink: 0 }}>
                <SearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contacts..."
                  size="sm"
                />
              </div>
            </div>
          )}

          {/* Content Area */}
          <div style={{ padding: 'var(--space-6)' }}>
            {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px'
              }}>
                <LoadingSpinner message="Loading contacts..." />
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: 'var(--space-12)',
                color: 'var(--danger)'
              }}>
                <p style={{ margin: 0, fontSize: 'var(--text-base)' }}>{error}</p>
              </div>
            ) : allContacts.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <Users size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)'
                }}>
                  No contacts found for this client
                </p>
              </div>
            ) : displayedContacts.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <Users size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)'
                }}>
                  No contacts found matching your search
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: 'var(--space-5)',
                }}
              >
                {displayedContacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => handleContactClick(contact)}
                    style={{
                      background: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-lg)',
                      padding: 'var(--space-4)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--gray-500)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--gray-600)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Card Header */}
                    <div
                      style={{
                        marginBottom: 'var(--space-3)',
                        paddingBottom: 'var(--space-3)',
                        borderBottom: '1px solid var(--gray-700)',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: 'var(--text-lg)',
                          fontWeight: 'var(--font-semibold)',
                          color: 'var(--gray-50)',
                          margin: 0,
                          marginBottom: contact.role ? 'var(--space-2)' : 0,
                        }}
                      >
                        {contact.name || 'Unknown Contact'}
                      </h3>
                      {contact.role && (
                        <Badge variant="info" size="sm">
                          {contact.role}
                        </Badge>
                      )}
                    </div>

                    {/* Card Body */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                      {/* Company */}
                      {contact.clientName && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                          }}
                        >
                          <Building2
                            size={16}
                            style={{
                              flexShrink: 0,
                              marginTop: '2px',
                              color: 'var(--gray-400)',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--gray-300)',
                              wordBreak: 'break-word',
                            }}
                          >
                            {contact.clientName}
                          </span>
                        </div>
                      )}

                      {/* Phone */}
                      {contact.phoneNumber && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                          }}
                        >
                          <Phone
                            size={16}
                            style={{
                              flexShrink: 0,
                              marginTop: '2px',
                              color: 'var(--gray-400)',
                            }}
                          />
                          <a
                            href={`tel:${contact.phoneNumber}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--cta-blue)',
                              textDecoration: 'none',
                              wordBreak: 'break-word',
                            }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                          >
                            {formatPhoneNumber(contact.phoneNumber)}
                          </a>
                        </div>
                      )}

                      {/* Email */}
                      {contact.email && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                          }}
                        >
                          <Mail
                            size={16}
                            style={{
                              flexShrink: 0,
                              marginTop: '2px',
                              color: 'var(--gray-400)',
                            }}
                          />
                          <a
                            href={`mailto:${contact.email}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--cta-blue)',
                              textDecoration: 'none',
                              wordBreak: 'break-word',
                            }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                          >
                            {contact.email}
                          </a>
                        </div>
                      )}

                      {/* Billing Indicator */}
                      {contact.billing !== undefined && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                          }}
                        >
                          <DollarSign
                            size={16}
                            style={{
                              flexShrink: 0,
                              marginTop: '2px',
                              color: 'var(--gray-400)',
                            }}
                          />
                          <Badge variant={contact.billing ? 'success' : 'neutral'} size="sm">
                            {contact.billing ? '✓ Billing Contact' : 'Non-billing'}
                          </Badge>
                        </div>
                      )}

                      {/* Notes */}
                      {contact.notes && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                            marginTop: 'var(--space-2)',
                            paddingTop: 'var(--space-2)',
                            borderTop: '1px solid var(--gray-700)',
                          }}
                        >
                          <FileText
                            size={16}
                            style={{
                              flexShrink: 0,
                              marginTop: '2px',
                              color: 'var(--gray-400)',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 'var(--text-sm)',
                              lineHeight: '1.4',
                              color: 'var(--gray-400)',
                              wordBreak: 'break-word',
                            }}
                          >
                            {contact.notes}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Custom Pagination - Matching ContactsTable */}
          {!loading && !error && allContacts.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-bg)',
                borderTop: '1px solid var(--gray-600)',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  {pageSize === 999999 ? (
                    `Showing all ${filteredContacts.length} contact${filteredContacts.length !== 1 ? 's' : ''}`
                  ) : (
                    `Page ${currentPage} of ${totalPages} (${filteredContacts.length} contact${filteredContacts.length !== 1 ? 's' : ''})`
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                    Show:
                  </span>
                  <Button
                    variant={pageSize === 20 ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => handlePageSizeChange(20)}
                  >
                    20
                  </Button>
                  <Button
                    variant={pageSize === 50 ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => handlePageSizeChange(50)}
                  >
                    50
                  </Button>
                  <Button
                    variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => handlePageSizeChange(999999)}
                  >
                    All
                  </Button>
                </div>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>

                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    const startPage = Math.max(1, currentPage - 2);
                    const endPage = Math.min(totalPages, currentPage + 2);

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>
                      );
                    }

                    return pages;
                  })()}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Contact Details Modal */}
      {showContactDetailsModal && selectedContact && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactDetailsModal}
          onClose={() => {
            setShowContactDetailsModal(false);
            setSelectedContact(null);
          }}
          onContactUpdated={handleContactUpdated}
        />
      )}
    </>
  );
};

export default ClientContactsModal;
